import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { formatDateToDdMmYyyy, getCurrentDate, saveBlob, cn } from '../../../lib/utils';
import { showErrorToast } from '../../../lib/toast';
import ButtonV2 from '../../../components/ui/button/v2';
import { InvestigationReportAPI, ReportType, CoverLetterInputs } from '../../../api/investigationReport';
import { useAppSelector } from '../../../app/hooks';
import { selectCaseHeader } from '../../caseinfo/logic/Slice';

interface ReportCoverLetterModalProps {
  reportType: ReportType;
  onClose: () => void;
}

interface CoverLetterInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  prefixLabel?: string;
  disabled?: boolean;
}

const CoverLetterInput: React.FC<CoverLetterInputProps> = ({
  value,
  onChange,
  placeholder,
  prefixLabel,
  disabled,
}) => (
  <div className="flex items-center gap-[6px] bg-[#F9FAFB] p-[8px] h-[36px] rounded-[8px] border-none outline-none shrink-0 font-[Inter]">
    {prefixLabel && (
      <span className="text-[14px] leading-[20px] font-medium text-[#0A0A0A] select-none shrink-0">
        {prefixLabel}
      </span>
    )}
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full bg-transparent text-[14px] leading-[20px] font-normal text-[#0A0A0A] placeholder-[#737373] border-none outline-none focus:outline-none focus:ring-0 p-0 disabled:opacity-50"
    />
  </div>
);

interface CoverLetterTextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  heightClass?: string;
  disabled?: boolean;
}

const CoverLetterTextarea: React.FC<CoverLetterTextareaProps> = ({
  value,
  onChange,
  placeholder,
  heightClass = 'h-[96px]',
  disabled,
}) => (
  <textarea
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    className={cn(
      'w-full bg-[#F9FAFB] text-[14px] leading-[20px] font-normal text-[#0A0A0A] placeholder-[#737373] p-[8px] rounded-[8px] resize-none border-none outline-none focus:outline-none focus:ring-0 transition-colors shrink-0 font-[Inter] disabled:opacity-50',
      heightClass
    )}
  />
);

export const ReportCoverLetterModal: React.FC<ReportCoverLetterModalProps> = ({
  reportType,
  onClose,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const caseData = useAppSelector(selectCaseHeader);

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<CoverLetterInputs>({
    addressee: '',
    regarding: '',
    reference: '',
    introduction: '',
    body: '',
    signatory_name: '',
  });

  const reportTitle = reportType === ReportType.PRELIMINARY_REPORT ? 'Preliminary Report' : 'Investigation Report';

  const handleChange = (field: keyof CoverLetterInputs, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDownload = async (coverLetterData?: CoverLetterInputs) => {
    try {
      if (!caseId || !caseData?.uin) return;
      setIsLoading(true);
      const file = await InvestigationReportAPI.download(
        caseId,
        reportType,
        caseData.uin,
        coverLetterData
      );
      saveBlob(file.blob, file.fileName);
      onClose();
    } catch (error: any) {
      showErrorToast(`Failed to download ${reportTitle.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipAndDownload = () => {
    if (isLoading) return;
    handleDownload({});
  };

  const handleDownloadReport = () => {
    if (isLoading) return;
    const coverLetter: CoverLetterInputs = {
      addressee: formData.addressee?.trim() || null,
      regarding: formData.regarding?.trim() || null,
      reference: formData.reference?.trim() || null,
      introduction: formData.introduction?.trim() || null,
      body: formData.body?.trim() || null,
      signatory_name: formData.signatory_name?.trim() || null,
    };
    handleDownload(coverLetter);
  };

  const renderHeader = () => (
    <div className="relative self-stretch shrink-0 flex flex-col items-start gap-[6px] pt-[24px] px-[24px] pb-[16px] border-b border-[#E5E5E5] bg-white">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="absolute top-[16px] right-[16px] flex items-center justify-center w-[16px] h-[16px] rounded-[2px] cursor-pointer focus:outline-none disabled:opacity-50"
      >
        <X size={16} className="text-[#0A0A0A] opacity-70" />
      </button>

      <span className="self-stretch text-[18px] leading-none font-semibold text-[#0A0A0A]">
        {reportTitle}
      </span>
      <span className="self-stretch text-[14px] leading-[20px] font-normal text-[#737373]">
        Add the formal letter details that will appear before the report content
      </span>
    </div>
  );

  const renderContent = () => (
    <div className="self-stretch flex-1 min-h-0 flex flex-col gap-[12px] px-[24px] pb-[16px] pt-0 overflow-y-auto">
      <div className="flex justify-end items-center py-[8px] gap-[10px] self-stretch shrink-0 mt-4">
        <span className="text-[14px] leading-[20px] font-normal text-[#0A0A0A] select-none">
          Date: {formatDateToDdMmYyyy(getCurrentDate())}
        </span>
      </div>

      <CoverLetterTextarea
        value={formData.addressee || ''}
        onChange={(e) => handleChange('addressee', e.target.value)}
        placeholder={`The Assistant General Manager\nBank Name\nOffice/Department\nMumbai`}
        heightClass="h-[96px]"
        disabled={isLoading}
      />

      <div className="flex items-center p-[8px] self-stretch shrink-0">
        <span className="text-[14px] leading-[20px] font-normal text-[#0A0A0A]">
          Dear Sir/Madam,
        </span>
      </div>

      <CoverLetterInput
        prefixLabel="Reg:"
        value={formData.regarding || ''}
        onChange={(e) => handleChange('regarding', e.target.value)}
        placeholder="Subject of the letter"
        disabled={isLoading}
      />

      <CoverLetterInput
        prefixLabel="Ref:"
        value={formData.reference || ''}
        onChange={(e) => handleChange('reference', e.target.value)}
        placeholder="Reference of the case"
        disabled={isLoading}
      />

      <CoverLetterTextarea
        value={formData.introduction || ''}
        onChange={(e) => handleChange('introduction', e.target.value)}
        placeholder="Introduction"
        heightClass="h-[60px]"
        disabled={isLoading}
      />

      <CoverLetterTextarea
        value={formData.body || ''}
        onChange={(e) => handleChange('body', e.target.value)}
        placeholder="Body of the letter - summary of the facts of the case, findings, staff accountability and the action taken"
        heightClass="h-[114px]"
        disabled={isLoading}
      />

      <div>
        <div className="flex items-center p-[8px] self-stretch shrink-0">
          <span className="text-[14px] leading-[20px] font-normal text-[#0A0A0A]">
            Yours Truly,
          </span>
        </div>

        <CoverLetterInput
          value={formData.signatory_name || ''}
          onChange={(e) => handleChange('signatory_name', e.target.value)}
          placeholder="Enter Name"
          disabled={isLoading}
        />
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="self-stretch shrink-0 flex items-center justify-between px-[24px] py-[16px] bg-white font-[Inter]">
      <button
        type="button"
        onClick={handleSkipAndDownload}
        disabled={isLoading}
        className="text-[14px] leading-[20px] font-medium text-[#3551F3] cursor-pointer hover:underline focus:outline-none disabled:opacity-50"
      >
        Skip and download the report only
      </button>

      <div className="flex items-center gap-[8px]">
        <ButtonV2
          variant="outline"
          size="md"
          onClick={onClose}
          disabled={isLoading}
          className="h-[36px] px-[16px] py-[8px] rounded-[8px] border-[#E5E5E5] text-[#0A0A0A] bg-white hover:bg-[#F9FAFB] text-[14px] leading-[20px] font-medium shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
        >
          Cancel
        </ButtonV2>

        <ButtonV2
          variant="gradient"
          size="md"
          onClick={handleDownloadReport}
          loading={isLoading}
          disabled={isLoading}
          className="h-[36px] px-[16px] py-[8px] rounded-[8px] text-[14px] leading-[20px] font-medium"
        >
          Download Report
        </ButtonV2>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(10,10,10,0.20)] p-4">
      <div className="w-[826px] max-w-full max-h-[90vh] flex flex-col items-start rounded-[10px] border border-[#E5E5E5] bg-white shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.10),0px_4px_6px_-4px_rgba(0,0,0,0.10)] overflow-hidden">
        {renderHeader()}
        {renderContent()}
        {renderFooter()}
      </div>
    </div>
  );
};